import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="auth-toggle"
//
// Swaps between the social and email auth views and animates the container's
// height between them, so selecting "email" (a taller form) grows the card
// smoothly instead of jumping. Respects prefers-reduced-motion.
export default class extends Controller {
  static targets = ["social", "email"];

  declare readonly socialTarget: HTMLElement;
  declare readonly emailTarget: HTMLElement;

  private animating = false;

  connect(): void {
    // If #email is in the URL, start on the email form directly.
    // Uses hash fragment (not query param) because Rodauth's Roda routing
    // may strip query params during internal redirects.
    if (window.location.hash === "#email") {
      this.socialTarget.classList.add("hidden", "opacity-0");
      this.emailTarget.classList.remove("hidden", "opacity-0");
    }
  }

  showEmailForm(event: Event): void {
    event.preventDefault();
    this.switchView(this.socialTarget, this.emailTarget);
  }

  showSocialForm(event: Event): void {
    event.preventDefault();
    this.switchView(this.emailTarget, this.socialTarget);
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  private switchView(from: HTMLElement, to: HTMLElement): void {
    if (this.animating) return;

    // Reduced motion (or no smooth-resize support): swap instantly.
    if (this.prefersReducedMotion()) {
      from.classList.add("hidden", "opacity-0");
      to.classList.remove("hidden");
      to.classList.remove("opacity-0");
      return;
    }

    this.animating = true;
    const el = this.element as HTMLElement;

    // Fade the outgoing view out first, then swap + resize.
    from.classList.add("opacity-0");

    window.setTimeout(() => {
      const startHeight = el.offsetHeight;

      // Swap visibility; the incoming view starts faded out.
      from.classList.add("hidden");
      to.classList.remove("hidden");
      to.classList.add("opacity-0");

      // Measure the incoming view's natural height, then snap back to the
      // start height. Both happen in one JS turn, so no intermediate frame
      // paints (no flicker) — we just have two values to transition between.
      el.style.height = "auto";
      const targetHeight = el.scrollHeight;
      el.style.height = `${startHeight}px`;
      el.style.overflow = "hidden";
      void el.offsetHeight; // force reflow so the start height registers

      el.style.transition = "height 280ms cubic-bezier(0.22, 1, 0.36, 1)";
      requestAnimationFrame(() => {
        el.style.height = `${targetHeight}px`;
        to.classList.remove("opacity-0"); // fade incoming in
      });

      const cleanup = () => {
        el.style.height = "";
        el.style.transition = "";
        el.style.overflow = "";
        el.removeEventListener("transitionend", onEnd);
        this.animating = false;
      };
      const onEnd = (e: TransitionEvent) => {
        if (e.target === el && e.propertyName === "height") cleanup();
      };
      el.addEventListener("transitionend", onEnd);
      // Fallback in case transitionend doesn't fire (e.g. identical heights).
      window.setTimeout(cleanup, 450);
    }, 150);
  }
}
